import { forwardRef, useId, type ComponentPropsWithoutRef } from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@/shared/lib/cn';
import { labelClass } from '@/shared/ui/core/styles';

export type SwitchSize = 'sm' | 'md' | 'lg' | 'small' | 'medium' | 'large';

export type SwitchProps = Omit<ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>, 'size'> & {
  label?: string;
  description?: string;
  size?: SwitchSize;
};

const sizeSpecs: Record<'sm' | 'md' | 'lg', {
  track: string;
  thumb: string;
  checkedTranslate: string;
  uncheckedTranslate: string;
}> = {
  sm: {
    track: 'w-[32px] h-[18px]',
    thumb: 'size-[14px]',
    checkedTranslate: 'data-[state=checked]:translate-x-[16px]',
    uncheckedTranslate: 'data-[state=unchecked]:translate-x-[2px]',
  },
  md: {
    track: 'w-[40px] h-[22px]',
    thumb: 'size-[18px]',
    checkedTranslate: 'data-[state=checked]:translate-x-[20px]',
    uncheckedTranslate: 'data-[state=unchecked]:translate-x-[2px]',
  },
  lg: {
    track: 'w-[48px] h-[26px]',
    thumb: 'size-[22px]',
    checkedTranslate: 'data-[state=checked]:translate-x-[24px]',
    uncheckedTranslate: 'data-[state=unchecked]:translate-x-[2px]',
  },
};

function normalizeSize(size?: SwitchSize): 'sm' | 'md' | 'lg' {
  if (size === 'small' || size === 'sm') return 'sm';
  if (size === 'large' || size === 'lg') return 'lg';
  return 'md';
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { className, label, description, size = 'md', id, disabled, ...props },
  ref,
) {
  const autoId = useId();
  const fieldId = id ?? autoId;
  const normalized = normalizeSize(size);
  const spec = sizeSpecs[normalized];

  return (
    <div
      className={cn(
        'flex items-center gap-3',
        label || description ? 'w-full justify-between' : 'shrink-0',
      )}
    >
      {(label || description) && (
        <div className="min-w-0 flex-1 flex flex-col gap-0.5">
          {label ? (
            <label
              htmlFor={fieldId}
              className={cn(labelClass, disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}
            >
              {label}
            </label>
          ) : null}
          {description ? (
            <p className={cn('text-caption text-text-muted m-0', disabled && 'opacity-60')}>
              {description}
            </p>
          ) : null}
        </div>
      )}
      <SwitchPrimitive.Root
        ref={ref}
        id={fieldId}
        disabled={disabled}
        className={cn(
          'peer relative inline-flex shrink-0 cursor-pointer items-center rounded-full border-0 p-0',
          'bg-border-strong transition-colors duration-200 ease-in-out',
          'data-[state=checked]:bg-primary hover:data-[state=checked]:bg-primary-hover',
          'hover:data-[state=unchecked]:bg-border-strong/80',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-bg',
          'disabled:cursor-not-allowed disabled:opacity-50',
          /* Expand hit area to ≥44px for accessibility on touch devices */
          'after:absolute after:inset-[-0.625rem] after:content-[""]',
          spec.track,
          className,
        )}
        {...props}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            'pointer-events-none block rounded-full bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.28)] transition-transform duration-200 ease-in-out',
            spec.thumb,
            spec.checkedTranslate,
            spec.uncheckedTranslate,
          )}
        />
      </SwitchPrimitive.Root>
    </div>
  );
});
